import React, { useEffect, useMemo } from 'react';

import { faPlus, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  TextInput,
  Textarea,
  Button,
  Stack,
  Group,
  Paper,
  Title,
  ActionIcon,
  Select,
  Switch,
  Alert,
  Badge,
  TagsInput
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { type Survey } from './types';
import { PostJSON } from '../../utils/request';

export interface SurveyFormData {
  name: string;
  description?: string;
  who: string | null;
  intent?: string | null;
  questions: { prompt: string }[];
  instructions?: string;
  tags?: string[];
  isEnabled?: boolean;
}

export interface SurveyFormProps {
  initialData?: Survey;
  onSuccess?: () => void;
  onCancel?: () => void;
  mode?: 'add' | 'edit';
}

interface SurveyResponse {
  success: boolean;
  survey: Survey;
}

const SurveyForm: React.FC<SurveyFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
  mode = 'add'
}) => {
  const whoOptions = useMemo(
    () => [
      { label: 'Aide', value: 'aide' },
      { label: 'Family', value: 'family' },
      { label: 'Other', value: 'other' }
    ],
    []
  );

  const intentOptions = useMemo(
    () => [
      { label: 'Preshift', value: 'preshift' },
      { label: 'Postshift', value: 'postshift' },
      { label: 'Other', value: 'other' }
    ],
    []
  );

  const form = useForm<SurveyFormData>({
    initialValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      who: initialData?.who || 'aide',
      intent: initialData?.intent || 'postshift',
      questions: initialData?.content?.questions?.map(q => ({ prompt: q.prompt })) || [{ prompt: '' }],
      instructions: initialData?.content?.instructions || '',
      tags: initialData?.tags || [],
      isEnabled: initialData?.isEnabled !== undefined ? initialData.isEnabled : true
    },
    validate: {
      name: (value) => (!value || value.trim().length === 0 ? 'Name is required' : null),
      who: (value) => (!value ? 'Who is required' : null),
      questions: {
        prompt: (value) => (!value || value.trim().length === 0 ? 'Question prompt is required' : null)
      }
    }
  });

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      form.setValues({
        name: initialData.name || '',
        description: initialData.description || '',
        who: initialData.who || null,
        intent: initialData.intent || null,
        questions: initialData.content?.questions?.map(q => ({ prompt: q.prompt })) || [{ prompt: '' }],
        instructions: initialData.content?.instructions || '',
        tags: initialData.tags || [],
        isEnabled: initialData.isEnabled !== undefined ? initialData.isEnabled : true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const handleSubmit = async (values: SurveyFormData) => {
    try {
      // Validate that at least one question has a prompt
      const validQuestions = values.questions.filter(q => q.prompt.trim().length > 0);
      if (validQuestions.length === 0) {
        notifications.show({
          title: 'Error',
          message: 'At least one question is required',
          color: 'red'
        });
        return;
      }

      const submitData = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        who: values.who || '',
        intent: values.intent || undefined,
        content: {
          questions: validQuestions,
          instructions: values.instructions?.trim() || undefined
        },
        tags: values.tags || undefined
      };

      if (mode === 'edit' && initialData?.id) {
        // Edit existing survey
        await PostJSON<SurveyResponse, { id: number; name: string; description?: string; who: string; intent?: string; content: { questions: { prompt: string }[]; instructions?: string }; tags?: string[]; isEnabled?: boolean }>(
          '/api/survey/edit',
          {
            id: initialData.id,
            ...submitData,
            isEnabled: values.isEnabled
          }
        );
      } else {
        // Add new survey
        await PostJSON<SurveyResponse, { name: string; description?: string; who: string; intent?: string; content: { questions: { prompt: string }[]; instructions?: string }; tags?: string[]; isEnabled?: boolean }>(
          '/api/survey/create',
          {
            ...submitData,
            isEnabled: values.isEnabled !== undefined ? values.isEnabled : true
          }
        );
        form.reset();
        form.setFieldValue('questions', [{ prompt: '' }]);
      }
      onSuccess?.();
    } catch (e) {
      notifications.show({
        title: 'Error',
        message: e instanceof Error ? e.message : `Failed to ${mode === 'edit' ? 'update' : 'create'} survey`,
        color: 'red'
      });
    }
  };

  const addQuestion = () => {
    form.insertListItem('questions', { prompt: '' });
  };

  const removeQuestion = (index: number) => {
    if (form.values.questions.length > 1) {
      form.removeListItem('questions', index);
    } else {
      notifications.show({
        title: 'Error',
        message: 'At least one question is required',
        color: 'red'
      });
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="lg">
        <Title order={3}>{mode === 'edit' ? 'Edit Survey' : 'Create Survey'}</Title>

        <Paper withBorder p="md">
          <Stack gap="md">
            <TextInput
              required
              label="Name"
              placeholder="Enter survey name"
              {...form.getInputProps('name')}
            />

            <Textarea
              label="Description"
              minRows={3}
              placeholder="Enter survey description"
              {...form.getInputProps('description')}
            />

            <Select
              required
              data={whoOptions}
              label="Who"
              placeholder="Select who this survey is for"
              {...form.getInputProps('who')}
            />

            <Select
              data={intentOptions}
              label="Intent"
              placeholder="Select survey intent"
              {...form.getInputProps('intent')}
            />

            <TagsInput
              label="Tags"
              placeholder="Enter tags"
              {...form.getInputProps('tags')}
            />
            <Alert icon={<FontAwesomeIcon icon={faExclamationTriangle} />}>
              Adding tags to the survey means this survey will only be used for clients that have all the tags listed. If you do not want to restrict the survey, leave the tags empty.
            </Alert>
          </Stack>
        </Paper>

        <Paper withBorder p="md">
          <Stack gap="md">
            <Group align="center" justify="space-between">
              <Title order={4}>Questions</Title>
              <Button
                leftSection={<FontAwesomeIcon icon={faPlus} />}
                size="sm"
                variant="light"
                onClick={addQuestion}
              >
                Add Question
              </Button>
            </Group>

            <Alert>
              In the question prompt, you may use the following placeholders that reference information specific to the client: <Badge>{'{{CLIENTNAME}}'}</Badge>
            </Alert>

            {form.values.questions.map((question, index) => (
              <Group key={index} align="flex-start" gap="sm">
                <Textarea
                  required
                  label={`Question ${index + 1}`}
                  minRows={2}
                  placeholder="Enter question prompt"
                  style={{ flex: 1 }}
                  {...form.getInputProps(`questions.${index}.prompt`)}
                />
                <ActionIcon
                  color="red"
                  disabled={form.values.questions.length === 1}
                  mt="xl"
                  variant="light"
                  onClick={() => removeQuestion(index)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        </Paper>

        <Paper withBorder p="md">
          <Stack gap="md">
            <Textarea
              label="Instructions"
              minRows={4}
              placeholder="Enter survey instructions"
              {...form.getInputProps('instructions')}
            />
          </Stack>
        </Paper>

        <Paper withBorder p="md">
          <Stack gap="md">
            <Switch
              label={form.values.isEnabled ? 'Enabled' : 'Disabled'}
              {...form.getInputProps('isEnabled', { type: 'checkbox' })}
            />
            <Alert>
              {form.values.isEnabled
                ? 'When enabled, this survey will be in the circulation immediately once saved.'
                : 'When disabled, this survey will not be in the circulation yet. You can make edits and enable it later.'}
            </Alert>
          </Stack>
        </Paper>

        <Group justify="flex-end">
          <Group gap="sm">
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit">
              {mode === 'edit' ? 'Save Changes' : 'Create Survey'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
};

export default SurveyForm;

